import type { KgReportSourceType } from "../../../../services/kg/kgDrawerApi.types";
import type { KgInspectorTableSourceRef } from "./types";
import type { KgInspectorIconKey } from "./kgInspectorIcons";
import type { InspectorTabConfig } from "./panelConfig";

/** Magic BI source row for native report overview (Figma — always Magic BI + local logo). */
export const MAGICBI_REPORT_SOURCE: KgInspectorTableSourceRef = {
  name: "Magic BI",
  logoUrl: "/logos/magicbi.svg",
};

/** Panel header subtitle per report `sourceType`. */
export const REPORT_SUBTITLE: Record<KgReportSourceType, string> = {
  datasource: "Report (Data sources)",
  magicbi: "Report (Native)",
  retrieved: "Report (Retrieved)",
};

/** Panel header leading icon per report `sourceType`. */
export const REPORT_HEADER_ICON: Record<KgReportSourceType, KgInspectorIconKey> = {
  datasource: "reportPanel",
  magicbi: "reportNativePanel",
  retrieved: "reportRetrievedPanel",
};

/** Whether to show `overview.source.logoUrl` beside the title in the panel header. */
export const REPORT_HEADER_SHOW_SOURCE_LOGO: Record<
  KgReportSourceType,
  boolean
> = {
  datasource: true,
  magicbi: false,
  retrieved: false,
};

export const shouldShowReportHeaderSourceLogo = (
  sourceType: KgReportSourceType | undefined,
): boolean =>
  sourceType !== undefined && REPORT_HEADER_SHOW_SOURCE_LOGO[sourceType];

const REPORT_BASE_TABS: InspectorTabConfig[] = [
  { id: "overview", label: "Overview" },
  { id: "trace", label: "Trace" },
  { id: "usedIn", label: "Used in" },
];

const REPORT_NATIVE_ACTIVITY_TAB: InspectorTabConfig = {
  id: "activity",
  label: "Activity",
};

/** Tab sets per report subtype — native (`magicbi`) adds Activity. */
export const getInspectorTabsForReport = (
  sourceType: KgReportSourceType | undefined,
): InspectorTabConfig[] =>
  sourceType === "magicbi"
    ? [...REPORT_BASE_TABS, REPORT_NATIVE_ACTIVITY_TAB]
    : REPORT_BASE_TABS;

/** Subtypes that render a full Overview tab (not “coming soon”). */
export const REPORT_OVERVIEW_SOURCE_TYPES: readonly KgReportSourceType[] = [
  "datasource",
  "magicbi",
  "retrieved",
] as const;

export const isReportNativeOverview = (
  sourceType: KgReportSourceType | undefined,
): boolean => sourceType === "magicbi";

/** Default expanded trace section id when a report node is selected. */
export const REPORT_TRACE_DEFAULT_EXPANDED: Partial<
  Record<KgReportSourceType, string>
> = {
  datasource: "dataSources",
  magicbi: "ontologies",
  retrieved: "ontologies",
};

export const isReportOverviewEnabled = (
  sourceType: KgReportSourceType | undefined,
): boolean =>
  sourceType !== undefined &&
  (REPORT_OVERVIEW_SOURCE_TYPES as readonly string[]).includes(sourceType);

export const getReportHeaderIcon = (
  sourceType: KgReportSourceType | undefined,
): KgInspectorIconKey =>
  sourceType ? REPORT_HEADER_ICON[sourceType] : "reportPanel";

export const getReportTraceDefaultExpanded = (
  sourceType: KgReportSourceType | undefined,
): Record<string, boolean> => {
  const sectionId = sourceType
    ? REPORT_TRACE_DEFAULT_EXPANDED[sourceType]
    : undefined;
  return sectionId ? { [sectionId]: true } : {};
};
